import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type {
  ProductModifierValue,
  SelectedElementDesignationTransformed,
} from "schemas/generated/fileModifiers";

import React from "react";

import { Badge } from "@replo/design-system/components/badge/Badge";
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxHeader,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTitle,
  ComboboxTrigger,
} from "@replo/design-system/components/combobox/Combobox";
import { ListItem } from "@replo/design-system/components/list-item/ListItem";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { skipToken } from "@tanstack/react-query";
import InfiniteScroll from "@v2/components/InfiniteScroll";
import { Loader } from "@v2/components/Loader";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useProductList } from "@v2/hooks/useProductList";
import { productRouter } from "@v2/trpc/routes/product";
import startCase from "lodash-es/startCase";
import { ChevronDown, ImageIcon, Plus, SearchIcon } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";
import { validate as isUUIDValid } from "uuid";

import { useSelectedElementDesignations } from "../store/ModifiersStoreContext";

function isProductDesignation(
  designation: SelectedElementDesignationTransformed,
): designation is SelectedElementDesignationTransformed & {
  type: "dynamicDataSource";
  data: { type: "product" };
} {
  return (
    designation.type === "dynamicDataSource" &&
    designation.data.type === "product"
  );
}

const PRODUCTS_LIMIT = 100;
const SKELETON_COUNT = 5;

const ProductSkeletons: React.FC = () => (
  <div className="flex flex-col gap-1 p-1">
    {Array.from({ length: SKELETON_COUNT }, (_, i) => (
      <Skeleton key={i} className="h-12 w-full rounded-md" />
    ))}
  </div>
);

const getProductStatusVariant = (
  status: string,
): "success" | "info" | "neutral" => {
  return exhaustiveSwitch(
    { status },
    "status",
  )({
    active: "success",
    draft: "info",
    archived: "neutral",
  });
};

interface ProductSelectorModifierProps {
  onChange: (modifiers: ProductModifierValue[]) => void;
}

const ProductSelectorModifier: React.FC<ProductSelectorModifierProps> = ({
  onChange,
}) => {
  const designations = useSelectedElementDesignations();
  const productDesignation = designations?.find((designation) => {
    return isProductDesignation(designation);
  });
  const projectId = useCurrentProjectId();
  const [isOpen, setIsOpen] = React.useState(false);
  const [, searchTerm, setSearchTerm] = useDebouncedState("");

  const [currentProductId, setCurrentProductId] = useOverridableState(
    productDesignation?.data.productId ?? "",
    React.useCallback(
      (productId: string) => {
        const modifiers: ProductModifierValue[] = [
          { type: "product", value: productId },
        ];
        onChange(modifiers);
      },
      [onChange],
    ),
  );

  const {
    products,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProductList({
    projectId,
    limit: PRODUCTS_LIMIT,
    search: searchTerm,
  });
  const {
    data: currentProduct,
    isLoading: isLoadingCurrentProduct,
    isFetching: isFetchingCurrentProduct,
  } = productRouter.get.useQuery(
    currentProductId && isUUIDValid(currentProductId)
      ? { type: "id", id: currentProductId }
      : skipToken,
  );

  const comboboxItems: ComboboxItemWithValue[] = React.useMemo(() => {
    return products.map((product) => ({
      value: product.id,
      label: product.title,
      size: "lg" as const,
      startEnhancer: (
        <Badge size="xl">
          {product.featuredMedia?.url ? (
            <img src={product.featuredMedia.url} alt="" />
          ) : (
            <ImageIcon className="text-muted size-4" />
          )}
        </Badge>
      ),
      description: (
        <span>
          <Tag size="sm" variant={getProductStatusVariant(product.status)}>
            {startCase(product.status)}
          </Tag>{" "}
          {product.slug ?? ""}
        </span>
      ),
    }));
  }, [products]);

  const selectedItem =
    comboboxItems.find((item) => item.value === currentProductId) ?? null;

  const handleChange = (item: ComboboxItemWithValue | null) => {
    if (item) {
      setCurrentProductId(item.value);
    }
  };

  const shouldShowLoadingIndicator =
    isLoadingCurrentProduct || isFetchingCurrentProduct;

  const shouldShowEmptyState =
    comboboxItems.length === 0 &&
    !isLoading &&
    !isFetchingNextPage &&
    !isFetchingCurrentProduct;

  return (
    <div className="w-full flex-1">
      <ComboboxRoot
        items={comboboxItems}
        value={selectedItem}
        onValueChange={handleChange}
        itemToStringValue={(item) => String(item.label)}
        isItemEqualToValue={(a, b) => a.value === b.value}
        filter={null}
        onInputValueChange={(input) => setSearchTerm(input)}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <ComboboxTrigger className="w-full">
          <ListItem
            label={currentProduct?.title ?? "Product"}
            description={
              currentProduct ? (
                <span className="truncate min-w-0 ">
                  <Tag
                    size="sm"
                    variant={getProductStatusVariant(currentProduct.status)}
                  >
                    {startCase(currentProduct.status)}
                  </Tag>{" "}
                  {currentProduct.slug}
                </span>
              ) : (
                "Select a product"
              )
            }
            startEnhancer={
              currentProduct?.featuredMedia?.url ? (
                <Badge size="xl">
                  <img
                    src={currentProduct.featuredMedia.url}
                    alt={currentProduct.title}
                  />
                </Badge>
              ) : (
                <Badge size="xl" className="border-dashed !bg-white">
                  {shouldShowLoadingIndicator ? (
                    <Loader className="size-4 text-muted" />
                  ) : (
                    <Plus className="text-muted size-4" />
                  )}
                </Badge>
              )
            }
            endEnhancer={<ChevronDown className="text-muted" />}
            size="lg"
            className="w-full"
            UNSAFE_className="border border-border border-0.5 items-center"
          />
        </ComboboxTrigger>
        <ComboboxContent sideOffset={4} side="bottom" align="start">
          <ComboboxHeader>
            <ComboboxTitle>Select Product</ComboboxTitle>
            <ComboboxInput
              startEnhancer={<SearchIcon size={16} />}
              placeholder="Search by product name, id, or SKU..."
            />
          </ComboboxHeader>
          {shouldShowEmptyState && <ComboboxEmpty />}
          <ComboboxList
            id="product-selector-scroll"
            UNSAFE_className="h-[350px]"
          >
            <InfiniteScroll
              dataLength={products.length}
              next={() => void fetchNextPage()}
              hasMore={hasNextPage}
              scrollableTarget="product-selector-scroll"
              loader={<ProductSkeletons />}
              className="flex flex-col gap-0.5"
            >
              {comboboxItems.map((item) => {
                const product = products.find((p) => p.id === item.value);
                return (
                  <Tooltip
                    key={item.value}
                    content={
                      <div className="flex w-[250px] flex-col gap-1">
                        <p className="font-medium">{product?.title}</p>
                        <p>Handle: {product?.slug}</p>
                        <p>ID: {product?.id}</p>
                      </div>
                    }
                    side="right"
                  >
                    <ComboboxItem {...item} value={item} />
                  </Tooltip>
                );
              })}
            </InfiniteScroll>
            {(isFetchingNextPage || isLoading) && <ProductSkeletons />}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
    </div>
  );
};

export default ProductSelectorModifier;
